import React, { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatINR } from "../../lib/format";

export default function Referral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [maxMilestoneBonus, setMaxMilestoneBonus] = useState(null);

  useEffect(() => {
    loadReferral();
    // Read the cap live from the tiers list instead of hardcoding it, so this
    // copy can't drift if MILESTONE_TIERS ever changes on the backend.
    api.get("/milestones/current")
      .then(({ data }) => {
        const tiers = data?.tiers || [];
        setMaxMilestoneBonus(tiers.length ? tiers[tiers.length - 1].bonus_paise : null);
      })
      .catch((e) => console.error("Failed to load milestone cap:", e));
  }, []);

  const loadReferral = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/referral");
      setData(response.data);
    } catch (err) {
      setError("Failed to load referral link");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const url = data?.referral_url;
    if (url) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (error) {
    return (
      <div className="space-y-4 max-w-lg">
        <h1 className="text-2xl font-bold">Referral Programme</h1>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
        <button onClick={loadReferral} className="bg-[#F97316] text-white px-4 py-2 rounded-lg">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-display">Referral Programme</h1>
        <p className="text-gray-500 text-sm mt-1">Earn rewards by referring tenants, agents and brokers to Orello.</p>
      </div>

      {/* Referral Link Box */}
      <div className="bg-[#FFF4ED] border border-indigo-200 rounded-xl p-4 space-y-3">
        <p className="text-sm font-medium text-indigo-800">Your referral link</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-white border border-indigo-200 rounded-lg px-3 py-2 text-sm text-gray-700 truncate">
            {loading ? "Loading..." : (data?.referral_url || "—")}
          </div>
          <button
            onClick={copyLink}
            disabled={loading || !data?.referral_url}
            className="p-2 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] disabled:opacity-50"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* How You Earn */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">How you earn</h2>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
            <div>
              <p className="text-sm font-medium">Tenant or landlord completes KYC</p>
              <p className="text-xs text-gray-500">Via your referral link</p>
            </div>
            <span className="text-sm font-semibold text-[#F97316]">+50 points</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
            <div>
              <p className="text-sm font-medium">Monthly milestones — flat bonus by agreements closed this cycle</p>
              <p className="text-xs text-gray-500">See your progress chart on Home &amp; Earnings — resets each month, capped at the top tier</p>
            </div>
            <span className="text-sm font-semibold text-green-600">
              Up to {maxMilestoneBonus != null ? formatINR(maxMilestoneBonus) : "—"} flat
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
            <div>
              <p className="text-sm font-medium">5 agents/brokers you refer each close their first paid deal</p>
              <p className="text-xs text-gray-500">One-time bonus</p>
            </div>
            <span className="text-sm font-semibold text-green-600">+Rs 250</span>
          </div>
        </div>
      </div>

      {/* Milestone Progress */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">Your progress</h2>
        <div className="space-y-2">
          <div className="p-3 bg-white border border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium">Qualified referred agents/brokers</p>
              <p className="text-sm font-semibold text-[#F97316]">
                {loading ? "—" : `${data?.qualified_referred_brokers_count || 0} / 5`}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              {loading
                ? " "
                : data?.broker_recruit_bonus_paid
                  ? "₹250 bonus already earned"
                  : "Each must close at least 1 paid deal to count"}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-[#F97316]">
            {loading ? "—" : (data?.points || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Leaderboard Points</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            Rs {loading ? "—" : ((data?.wallet_credits_earned || 0) / 100).toLocaleString("en-IN")}
          </div>
          <div className="text-xs text-gray-500 mt-1">Wallet Earned</div>
        </div>
      </div>
    </div>
  );
}
